import {
	Breadcrumb,
	IBreadcrumbItem,
	IDropdownOption,
	IRawStyle,
	ITextStyles,
	Icon,
	IconButton,
	SelectionMode,
	Stack,
	Sticky,
	StickyPositionType,
	Text,
	mergeStyles,
} from '@fluentui/react';
import {useBoolean} from '@fluentui/react-hooks';
import {useUserContext} from 'authentication/UserContext';
import {
	checkSearchValid,
	DetailsField,
	DetailsSection,
	HEIGHT_OF_LIST_HEADER,
	PdfViewer,
} from 'components';
import {AuditLogsLoadingStatusProvider} from 'components/ChangeHistory/AuditLogsLoadingStatusProvider';
import {CommentForm} from 'components/CommentForm';
import {CommentLevel} from 'components/CommentList';
import {FAQForm} from 'components/FAQForm';
import {FAQLevel} from 'components/FAQList';
import {useSortRegulatoryDocs} from 'features/Regulations/components/useSortRegulatoryDocs';
import {RegDocParagraphTooltipTranslationProvider} from 'features/localizedTooltips';
import {mapToRef} from 'helpers';
import {
	StateForShouldShowAudit,
	useCommand,
	useFavoriteCommand,
	useShouldShowAudit,
} from 'hooks';
import {useToggleAuditCmd} from 'hooks/audit/useToggleAuditCmd';
import React, {useCallback, useContext, useMemo, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {TFunction, useTranslation} from 'react-i18next';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {
	CorrectStickyType,
	DocumentStatus,
	Market,
	RegulatoryDocument,
	RegulatoryDocumentParagraph,
	UserRole,
	Workflow,
	WorkflowStatus,
} from 'types';
import ParagraphsComparisonPanel from '../ParagraphsComparisonPanel';
import {SidePaneForParagraphs} from '../SidePaneForParagraphs';
import {CompareVersionPanel} from '../components/DocumentCompare';
import {
	CompareversionForm,
	EditParagraphsForm,
	KeywordAssignmentForm,
	ParagraphTableOfContents,
	RemoveRequirementsForm,
	ViewParagraphsForm,
	WorkflowDialog,
} from '../components/DocumentDetails';
import {AssignRequirementsForm} from '../components/DocumentDetails/AssignRequirementsForm';
import {
	ParagraphsContextProvider,
	useParagraphsContext,
} from '../components/DocumentDetails/ParagraphsContext';
import {RegulatoryDocumentsForm} from '../components/RegulatoryDocumentsForm/RegulatoryDocumentsForm';
import {
	RegulatoryDocumentFormInfo,
	useRegulatoryDocumentForm,
} from '../components/RegulatoryDocumentsForm/useRegulatoryDocumentForm';
import {
	EditorSelectionContextProvider,
	RegulatoryDocumentsContextProvider,
} from '../context';
import {useAssignRegulatoryDocumentParagraphsTagsMutation} from '../hooks/useAssginRegulatoryDocumentParagraphsTag.generated';
import {useAssignRegulatoryDocumentParagraphsRequirementsMutation} from '../hooks/useAssignRegulatoryDocumentParagraphsRequirement.generated';
import {
	GetRegulatoryDocumentDetailsDocument,
	GetRegulatoryDocumentDetailsQuery,
	GetVexRegulatoryDocumentParagraphsQuery,
	useGetRegulatoryDocumentDetailsQuery,
	useGetVexRegulatoryDocumentParagraphsQuery,
} from '../hooks/useGetRegulatoryDocumentDetails.generated';
import {useUpdateRegulatoryDocumentWorkflowStatusMutation} from '../hooks/useUpdateRegulatoryDocumentWorkflowStatus.generated';
import {useCreateInterimRequirementMutation} from '../hooks/useCreateInterimRequirment.generated';
import {getStackHeight} from '../paragraphsComparisonStyles';
import {
	getIfDocHasUneditableStatus,
	useEditRegulatoryDocCmd,
} from '../useEditRegulatoryDocCmd';
import {
	EditParagraphsFormSubmissionStatusContext,
	EditParagraphsFormSubmissionStatusInfo,
	EditParagraphsFormSubmissionStatusProvider,
} from './EditParagraphsFormSubmissionStatusProvider';
import {RegDocDetailsPageLoadWrapper} from './LoadWrapper/RegDocDetailsPage.LoadWrapper';
import {ParagraphFieldsSortingService} from './ParagraphFieldsSorting.service';
import {buildRegulatoryDocumentFieldRows} from './RegDocDetailsFields';
import {RegDocDetailsPageParagraphsList} from './RegDocDetailsPage.ParagraphsList';
import {useRegDocDetailsPageTranslations} from './RegDocDetailsPage.commonHooks';
import {
	CLASS_TO_ADD_TO_PARAGRAPHS_SECTION_HEADER_WHEN_STICKY,
	HEIGHT_OF_PARAGRAPHS_SECTION_HEADER,
} from './RegDocDetailsPage.constants';
import {RegDocDetailsPageInfoProvider} from './RegDocDetailsPage.context';
import {DocOfRegDocDetailsPageQuery} from './RegDocDetailsPage.queryTypes';
import {RefToContainerOfMainParagraphs} from './RegDocDetailsPage.types';
import {RegDocSidebarInfoProvider} from './RegDocSidebarInfoProvider';
import {ViewPdfButton} from './ViewPdfButton';
import {
	RegDocAuditLogsInfo,
	useGetRegDocAuditLogs,
} from './useGetRegDocAuditLogs';
import {
	getCorrectStatusName,
	getDisabledEditionForMOdifiedVersionTooltip,
	isRegDocEditDisabled,
	isRegDocEditVersionDataDisabled,
} from '../regulatoryUtils';
import {DocumentSourceEnum} from 'features/Regulations/RegulationDetail/RegulationDetail.types';
import {EntityListContextProvider} from 'components/EntityList/EntityListContext';
import {ParagraphGridView} from '../components/GridView/ParagraphGridView';
import {RegulatoryDocumentsCollectionName} from 'components/Faq/EntityTypeMapper';
import {useQuestionCountsByEntityTypeQuery} from 'components/Faq/Faq.generated';
import {FaqPanelMode} from 'components/Faq/FaqPanel';
import useQuestionsPermissions from 'components/Faq/hooks/useQuestionsPermissions';
import {IComparedItem} from '../components/DocumentCompare/compareDocuments/compareDocuments';
import {useParagraphMetadataUpdader} from '../hooks/useParagraphMetadataUpdater';
import {ComparisonUpdateFields} from '../components/DocumentCompare/CompareVersionPanel/compareVersionTypes';
import {useSearchParagraphsByRegDocIdLazyQuery} from 'features/Search/hooks/useFullTextSearch.generated';

export const RegulatoryDocumentDetailsPage: React.FC = () => {
	return (
		<ParagraphsContextProvider>
			<EditorSelectionContextProvider>
				<EditParagraphsFormSubmissionStatusProvider>
					<RegulatoryDocumentDetailsPageComponent />
				</EditParagraphsFormSubmissionStatusProvider>
			</EditorSelectionContextProvider>
		</ParagraphsContextProvider>
	);
};

const useEnumTranslation = (keyPrefix: string) => {
	return useTranslation('common/enums', {keyPrefix});
};

// eslint-disable-next-line complexity
const RegulatoryDocumentDetailsPageComponent: React.FC = () => {
	/**
	 * Translations
	 */
	const {t} = useRegDocDetailsPageTranslations();
	const {t: getWorkflowStatus} = useEnumTranslation('WorkflowStatus');
	const {t: getStatusTranslation} = useEnumTranslation(
		'RegulatoryDocumentStatus',
	);
	const {t: tPogis} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'Reference',
	});
	const {t: getDisassemblyStatusTranslation} = useEnumTranslation(
		'RegulatoryDocumentDisassemblyStatus',
	);

	const [updateRegulatoryDocumentWorkflowStatus] =
		useUpdateRegulatoryDocumentWorkflowStatusMutation();
	const [createInterimRequirement] = useCreateInterimRequirementMutation();
	const [searchParagraphs, {loading: loadingSearch, data: dataSearch}] =
		useSearchParagraphsByRegDocIdLazyQuery();
	/**
	 * Other hooks
	 */
	const {shouldShowFaq} = useQuestionsPermissions();
	const {updateParagraphMetadata} = useParagraphMetadataUpdader();
	const [isFormRefetchingRegDoc, setIsFormRefetchingRegDoc] =
		useState<boolean>(false);
	const navigate = useNavigate();
	const {isVex, isVko, myVexClusters, isAdmin, username, encodedUserId} =
		useUserContext();

	const myVexClusterIds = myVexClusters.flatMap(c => c.id);

	const [sideBySide, setSideBySide] = React.useState<RegulatoryDocument>();
	const handleSideBySideChange = (newValue: RegulatoryDocument) => {
		setSideBySide(newValue);
	};

	const [viewPdf, {toggle: toggleViewPdf}] = useBoolean(false);
	const [viewGrid, setViewGrid] = useState(false);

	const [tocIsOpen, {setFalse: hideToc, toggle: toggleToc}] = useBoolean(false);
	const {
		textStyles,
		stackStyles,
		stackshit,
		mainParagraphsWrapper,
		pdfParagraphsListWrapper,
	} = getStyles(Boolean(sideBySide) || viewPdf);

	const stateForShouldShowAudit: StateForShouldShowAudit = useShouldShowAudit();
	const [showAudit] = stateForShouldShowAudit;

	const {regulatoryDocumentId: docIdWithoutType} = useParams();
	const docId = docIdWithoutType as string;

	const {
		loading: isRegDocLoading,
		data,
		networkStatus: regDocNetworkStatus,
	} = useGetRegulatoryDocumentDetailsQuery({
		variables: {regulatoryDocumentId: docId},
		notifyOnNetworkStatusChange: true,
	});

	const [searchParams] = useSearchParams();

	const filter = searchParams.get('filter') ?? '';
	const searchString = searchParams.get('text') ?? '';

	const refToContainerOfMainParagraphs: RefToContainerOfMainParagraphs =
		useRef(null);

	/**
	 * Queries
	 */

	const [assignRegulatoryDocumentParagraphsRequirements] =
		useAssignRegulatoryDocumentParagraphsRequirementsMutation();

	const [assignRegulatoryDocumentParagraphsTags] =
		useAssignRegulatoryDocumentParagraphsTagsMutation();

	const {
		data: vexItems,
		loading: vexItemsLoading,
		networkStatus: vexItemsNetworkStatus,
	} = useGetVexRegulatoryDocumentParagraphsQuery({
		variables: {regulatoryDocumentId: docId, inProcess: filter === 'inProcess'},
		notifyOnNetworkStatusChange: true,
	});

	const {
		auditLogs,
		refetchAuditLogsAndUpdateIsLoading,
		isLoading: areAuditLogsLoading,
		setAreAuditLogsLoading,
	}: RegDocAuditLogsInfo = useGetRegDocAuditLogs(docId);

	/**
	 * Other
	 */
	const {sortParagraphsInRegDoc, sortParagraphs} =
		new ParagraphFieldsSortingService();

	type VexRegDocParagraphs =
		GetVexRegulatoryDocumentParagraphsQuery['vexRegulatoryDocumentParagraphs'];

	const getVexWorkbenchItems = (): VexRegDocParagraphs => {
		const paragraphs: VexRegDocParagraphs =
			vexItems?.vexRegulatoryDocumentParagraphs ?? [];
		return sortParagraphs(paragraphs);
	};

	const vexWorkbenchItems: VexRegDocParagraphs = React.useMemo(
		getVexWorkbenchItems,
		[sortParagraphs?.length, vexItems?.vexRegulatoryDocumentParagraphs],
	);

	const [showWorkbenchItems, setShowWorkbenchItems] = React.useState(
		(isVex && vexWorkbenchItems.length > 0) ?? false,
	);

	const getRegulatoryDocumentDetails = () => {
		type RegDoc = GetRegulatoryDocumentDetailsQuery['regulatoryDocument'];
		const regDoc: RegDoc = data?.regulatoryDocument;
		if (regDoc) return sortParagraphsInRegDoc(regDoc);
		return {} as RegulatoryDocument;
	};

	const regulatoryDocumentDetails = React.useMemo(
		getRegulatoryDocumentDetails,
		[data],
	);

	const comments = React.useMemo(
		() => regulatoryDocumentDetails.comments,
		[regulatoryDocumentDetails],
	);

	const faqs = React.useMemo(() => {
		const faqsData = regulatoryDocumentDetails.faqs;
		return faqsData;
	}, [regulatoryDocumentDetails]);

	const {data: questionSummaryData} = useQuestionCountsByEntityTypeQuery({
		variables: {
			entityType: RegulatoryDocumentsCollectionName,
			parentId: regulatoryDocumentDetails?.regulation?.id,
		},
	});

	const isPogisDocument = React.useMemo(
		() =>
			regulatoryDocumentDetails.documentSource?.name?.toLowerCase() ===
			DocumentSourceEnum.Pogis.toLowerCase(),
		[regulatoryDocumentDetails],
	);

	const {
		selectedParagraphs,
		setSelectedParagraphs,
		secondSelectedParagraphs,
		filteredParagraphs,
		setIndexToScrollTo,
	} = useParagraphsContext();

	React.useEffect(() => {
		setShowWorkbenchItems(isVex && vexWorkbenchItems.length > 0);
	}, [isVex, vexWorkbenchItems]);

	const paragraphs = React.useMemo(
		() =>
			showWorkbenchItems
				? (vexWorkbenchItems as RegulatoryDocumentParagraph[])
				: (regulatoryDocumentDetails.paragraphs as RegulatoryDocumentParagraph[]),
		[
			regulatoryDocumentDetails.paragraphs,
			showWorkbenchItems,
			vexWorkbenchItems,
		],
	);

	React.useEffect(() => {
		if (checkSearchValid(searchString)) {
			searchParagraphs({
				variables: {regulatoryDocumentId: docId, searchString},
			});
		}
	}, [searchString]);

	const paragraphListItems = React.useMemo(() => {
		if (filteredParagraphs.length > 0) {
			return filteredParagraphs;
		}

		if (checkSearchValid(searchString)) {
			const searchedParaIDs =
				dataSearch?.searchParagraphsForRegDoc.map(x => x.id) ?? [];
			return (paragraphs ?? []).filter(x => searchedParaIDs?.includes(x.id));
		}

		return paragraphs;
	}, [filteredParagraphs, paragraphs, dataSearch, searchString]);

	// We need this in order to update the SelectedParagraphs if there are changes in the data
	React.useEffect(() => {
		if (!paragraphListItems) return;
		const selectedParagraphsIds = selectedParagraphs.map(x => x.id);
		const selectedParagraphsUpdatedValues = paragraphListItems.filter(
			x => selectedParagraphsIds.indexOf(x.id) >= 0,
		);
		setSelectedParagraphs(selectedParagraphsUpdatedValues);
	}, [paragraphListItems]);

	const allBreadCrumbItems: IBreadcrumbItem[] = [
		{
			text: t('Regulations'),
			key: 'regulations',
			href: '/regulations',
		},
		{
			text: `${regulatoryDocumentDetails?.regulation?.regulationNumber}`,
			key: 'regulatoryDocument',
			href: `/regulations/${regulatoryDocumentDetails?.regulation?.id}`,
		},
		{
			text: `${regulatoryDocumentDetails?.name}`,
			key: 'name',
		},
	];

	const headlessBreadCrumbItems: IBreadcrumbItem[] = [
		{
			text: t('RegulatoryDocuments'),
			key: 'regulatoryDocuments',
		},
		{
			text: `${regulatoryDocumentDetails?.name}`,
			key: 'name',
		},
	];

	const findDocumentValue = <
		Key extends keyof typeof regulatoryDocumentDetails,
	>(
		key: Key,
	): RegulatoryDocument[Key] | undefined => {
		return regulatoryDocumentDetails?.[key];
	};

	const getStatusValue = (): DetailsField['data'] => {
		const status: RegulatoryDocument['status'] | undefined =
			findDocumentValue('status');
		return status ? getStatusTranslation(status) : null;
	};

	const getDisassemblyStatusValue = (): DetailsField['data'] => {
		const status: RegulatoryDocument['disassemblyStatus'] | undefined =
			findDocumentValue('disassemblyStatus');
		return status ? getDisassemblyStatusTranslation(status) : null;
	};

	interface FieldThatSupportsHistory extends DetailsField {
		auditFieldName: string;
		audit: boolean;
	}

	const createFieldThatSupportsHistory = (
		field: Omit<FieldThatSupportsHistory, 'audit'>,
	): FieldThatSupportsHistory => {
		return {...field, audit: showAudit};
	};

	const {control: WorkflowStatusControl} = useForm<Workflow>({
		reValidateMode: 'onSubmit',
		mode: 'onBlur',
	});

	const handleWorkflowStatusChange = useCallback(
		async (
			_event: React.FormEvent<HTMLDivElement>,
			option?: IDropdownOption,
		) => {
			if (option === undefined) {
				return;
			}

			await updateRegulatoryDocumentWorkflowStatus({
				variables: {
					regulatoryDocumentId: regulatoryDocumentDetails.id,
					workflowStatus: option.key as WorkflowStatus,
				},
			})
				.then(() => {
					if (option.key === WorkflowStatus.Finalized) {
						createInterimRequirement({
							variables: {regulatoryDocumentId: regulatoryDocumentDetails.id},
						});
					}
				})
				.then(() => window.location.reload());
		},
		[updateRegulatoryDocumentWorkflowStatus, regulatoryDocumentDetails.id],
	);

	const reapprovingHistoryMessages = React.useMemo(
		() =>
			auditLogs
				.filter(
					log =>
						log.change.key === 'reapprovingMessage' &&
						log.change.value &&
						['ADD', 'UPDATE'].indexOf(log.change.type) !== -1,
				)
				.sort((log1, log2) =>
					new Date(log1.createdAt).getTime() <
					new Date(log2.createdAt).getTime()
						? 1
						: -1,
				)
				.map(log => {
					return {
						date: new Date(log.createdAt),
						value: log.change.value,
					};
				}),
		[auditLogs],
	);

	const attachmentFaqLine = React.useMemo(() => {
		const attachmentField = {
			data: regulatoryDocumentDetails.attachments,
			type: 'attachments' as const,
			label: t('AttachmentsLabel'),
			rowNumber: 5,
		};
		return shouldShowFaq
			? [
					attachmentField,
					{
						data: {
							...questionSummaryData?.questionCountsByEntityType?.find(
								t => t.entityId === docId,
							),
							faqState: {
								mode: FaqPanelMode.RegDocDetails,
								entity: regulatoryDocumentDetails,
								showPanel: true,
							},
							text: t('FaqText'),
						},
						type: 'faq' as const,
						label: t('FaqLabel'),
					},
			  ]
			: [attachmentField];
	}, [
		docId,
		questionSummaryData?.questionCountsByEntityType,
		regulatoryDocumentDetails.attachments,
		shouldShowFaq,
		t,
	]);

	const regDocFieldRows: DetailsField[][] = React.useMemo(
		() =>
			buildRegulatoryDocumentFieldRows({
				regulatoryDocumentDetails: regulatoryDocumentDetails as any,
				t,
				tPogis,
				getWorkflowStatus,
				showAudit,
				isAdmin,
				reapprovingHistoryMessages,
				areAuditLogsLoading,
				getCorrectStatusName,
				getStatusValue,
				getDisassemblyStatusValue,
				WorkflowStatusControl,
				handleWorkflowStatusChange,
				attachmentFaqLine: attachmentFaqLine as any,
				tDisclaimer: t('Disclaimer'),
			}),
		[
			regulatoryDocumentDetails,
			showAudit,
			t,
			getWorkflowStatus,
			auditLogs,
			areAuditLogsLoading,
			questionSummaryData,
			attachmentFaqLine,
			isAdmin,
			reapprovingHistoryMessages,
		],
	);

	const generalTabFieldLabels = React.useMemo(
		() => [
			t('ShortSummaryLabel'),
			t('ChangeHistoryLabel'),
			t('StatusLabel'),
			t('Approver'),
			t('WorkflowStatus'),
			tPogis('AlreadyCommunicated'),
			tPogis('pogisDocuments'),
			t('DisassemblyStatus'),
			t('DocumentSourceLabel'),
			t('AttachmentsLabel'),
			t('CreationDateLabel'),
			t('ChangeDateLabel'),
			t('KeywordsLabel'),
		],
		[t, tPogis],
	);

	const dateFieldsLabels = React.useMemo(
		() => [
			t('EffectiveDateLabel'),
			t('ExpirationDateLabel'),
			t('NewTypesLabel'),
			t('NewRegistrationLabel'),
			t('PhaseInLabel'),
			t('PhaseOutLabel'),
			t('ModelYearLabel'),
		],
		[t],
	);

	const referenceFieldLabels = React.useMemo(
		() => [t('DocumentReferences'), t('DocumentReferencedBy')],
		[t],
	);

	/**
	 * * Commands
	 */
	const {isSubmitting} = useContext(
		EditParagraphsFormSubmissionStatusContext,
	) as EditParagraphsFormSubmissionStatusInfo;

	useCommand(
		{
			key: 'close',
			text: t('CancelSecondVersion'),
			farCommand: true,
			onClick() {
				setSideBySide(undefined);
			},
			iconProps: {
				iconName: 'Cancel',
			},
			hidden: !sideBySide,
		},
		[sideBySide],
	);

	/**
	 * If you wish to pass any props to the button, edit the btn component
	 * directly. If you define props here, they might not be passed
	 */

	useCommand(
		{
			key: 'pdfView',
			farCommand: true,
			priority: 1,
			hidden:
				!regulatoryDocumentDetails.document ||
				isPogisDocument ||
				regulatoryDocumentDetails.documentStatus === DocumentStatus.NotSplit ||
				viewGrid,
			roles: [
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.Vex,
				UserRole.VexSupport,
				UserRole.ServiceProvider,
				UserRole.ShApprover,
				UserRole.Readers,
				UserRole.RegulationReader,
				UserRole.Bedarfskoordinator,
				UserRole.Avk,
			],
			commandBarButtonAs(): JSX.Element {
				return (
					<ViewPdfButton
						viewPdf={viewPdf}
						togglePdf={toggleViewPdf}
						sideBySide={sideBySide}
						doc={regulatoryDocumentDetails.document}
						refToContainerOfMainParagraphs={refToContainerOfMainParagraphs}
						setIndexToScrollTo={setIndexToScrollTo}
					/>
				);
			},
		},
		[
			viewPdf,
			viewGrid,
			sideBySide,
			regulatoryDocumentDetails.document,
			toggleViewPdf,
			refToContainerOfMainParagraphs,
			setIndexToScrollTo,
		],
	);

	useCommand(
		{
			key: 'copyRequirements',
			text: t('CopyRequirements'),
			priority: 7,
			onClick() {
				const myVexClusterRequirements =
					secondSelectedParagraphs[0]?.requirements?.filter(r =>
						r.vexClusters?.find(v => myVexClusterIds?.includes(v.id)),
					);

				assignRegulatoryDocumentParagraphsRequirements({
					variables: {
						input: {
							paragraphIds: selectedParagraphs?.map(sp => sp.id),
							regulatoryDocumentId: docId,
							requirementRefs: mapToRef(
								isVex
									? myVexClusterRequirements
									: secondSelectedParagraphs[0]?.requirements,
							),
						},
					},
					refetchQueries: [GetRegulatoryDocumentDetailsDocument],
				});
			},
			hidden: !sideBySide || isPogisDocument,
			disabled: !(
				selectedParagraphs.length > 0 && secondSelectedParagraphs.length === 1
			),
			iconProps: {
				iconName: 'Copy',
			},
		},
		[sideBySide, selectedParagraphs, secondSelectedParagraphs, isVex],
	);

	useCommand(
		{
			key: 'copyTags',
			text: t('CopyTags'),
			priority: 8,
			onClick() {
				assignRegulatoryDocumentParagraphsTags({
					variables: {
						input: {
							paragraphIds: selectedParagraphs?.map(sp => sp.id),
							regulatoryDocumentId: docId,
							tagRefs: mapToRef(secondSelectedParagraphs[0].tags),
						},
					},
					refetchQueries: [GetRegulatoryDocumentDetailsDocument],
				});
			},
			hidden: !sideBySide,
			disabled:
				!(
					selectedParagraphs.length > 0 && secondSelectedParagraphs.length === 1
				) || isPogisDocument,
			iconProps: {
				iconName: 'Copy',
			},
		},
		[sideBySide, selectedParagraphs, secondSelectedParagraphs],
	);
	useCommand(
		{
			key: 'gridView',
			text: t('GridView'),
			hidden:
				viewGrid ||
				![
					WorkflowStatus.Clearing,
					WorkflowStatus.ClearingWithReservations,
					WorkflowStatus.Examination,
					WorkflowStatus.InProgressInternal,
					WorkflowStatus.InProgressExternal,
					WorkflowStatus.Modified,
				].includes(regulatoryDocumentDetails?.workflow?.status),
			disabled: isRegDocEditDisabled(
				regulatoryDocumentDetails,
				isVko,
				encodedUserId,
			),
			onClick() {
				setViewGrid(true);
			},
			farCommand: true,
			priority: -1,
			roles: [UserRole.SystemAdministrator, UserRole.Vko],
			iconProps: {
				iconName: 'GridViewSmall',
			},
		},
		[viewGrid, regulatoryDocumentDetails],
	);

	useFavoriteCommand(docId, []);

	useCommand(
		{
			key: 'workbenchItems',
			farCommand: true,
			priority: 0,
			onClick() {
				setShowWorkbenchItems(!showWorkbenchItems);
			},
			hidden: !isVex || isPogisDocument,
			iconProps: {iconName: 'Workflow'},
			title: showWorkbenchItems ? t('ShowAllParagraphsTooltip') : '',
		},
		[showWorkbenchItems, isVex],
	);

	useCommand(
		{
			key: 'openDocument',
			text: t('OpenEditor'),
			priority: 5,
			iconProps: {
				iconName: 'OpenFile',
			},
			onClick: () =>
				navigate(
					`/regulations/${regulatoryDocumentDetails?.regulation?.id}/${docIdWithoutType}`,
				),
			hidden:
				(regulatoryDocumentDetails?.workflow?.status !== WorkflowStatus.New &&
					regulatoryDocumentDetails?.workflow?.status !==
						WorkflowStatus.QualityControlInternal &&
					regulatoryDocumentDetails?.workflow?.status !==
						WorkflowStatus.QualityControlExternal) ||
				isPogisDocument ||
				isVko,
			disabled:
				regulatoryDocumentDetails?.workflow?.status ===
				WorkflowStatus.Finalized,
			roles: [
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.ServiceProvider,
			],
		},
		[docIdWithoutType, regulatoryDocumentDetails, navigate],
	);

	useToggleAuditCmd(stateForShouldShowAudit);

	/**
	 * Edit form
	 */
	const formInfo: RegulatoryDocumentFormInfo = useRegulatoryDocumentForm();
	const {
		modeStateInfo: {setMode},
		documentChangeStateInfo: {setDidDocumentChange},
	} = formInfo;

	const {regulation} = regulatoryDocumentDetails;

	const isEditVersionDataDisabled = useMemo(() => {
		return (
			getIfDocHasUneditableStatus(
				regulatoryDocumentDetails,
				isAdmin,
				username,
			) ||
			isRegDocEditVersionDataDisabled(
				regulatoryDocumentDetails,
				isAdmin,
				isVko,
				encodedUserId,
			) ||
			isSubmitting
		);
	}, [
		regulatoryDocumentDetails,
		isAdmin,
		username,
		encodedUserId,
		isSubmitting,
	]);

	useEditRegulatoryDocCmd({
		setDidDocumentChange,
		setMode,
		title: getDisabledEditionForMOdifiedVersionTooltip(
			regulatoryDocumentDetails,
			t,
		),
		key: 'editDoc',
		text: t('EditBtnLabel'),
		priority: 2,
		disabled: isEditVersionDataDisabled,
		hidden: isPogisDocument || viewGrid,
		extraDependencies: [isEditVersionDataDisabled, viewGrid],
	});

	type DocRegulation = NonNullable<DocOfRegDocDetailsPageQuery['regulation']>;

	type PageRegulatoryDocs = DocRegulation['regulatoryDocuments'];

	type DocOfPageRegulatoryDocs = PageRegulatoryDocs[number];

	const sortedRegulatoryDocs: PageRegulatoryDocs =
		useSortRegulatoryDocs<DocOfPageRegulatoryDocs>({
			docs: regulatoryDocumentDetails.regulation?.regulatoryDocuments,
			extraDependencies: [regulatoryDocumentDetails],
		});

	/*
	 * Other
	 */
	const isLoading: boolean = isRegDocLoading || vexItemsLoading;

	const renderBaseParagraphSectionHeader = (): JSX.Element => {
		return (
			<ParagraphsSectionHeader
				{...{
					filteredParagraphs,
					showWorkbenchItems,
					t,
					textStyles,
					toggleToc,
				}}
			/>
		);
	};

	const renderDefaultParagraphSectionHeader = (
		paragraphsHeader: JSX.Element,
	): JSX.Element => {
		const StickyWithCorrectType = Sticky as unknown as CorrectStickyType;

		return (
			<StickyWithCorrectType
				stickyPosition={StickyPositionType.Header}
				stickyClassName={CLASS_TO_ADD_TO_PARAGRAPHS_SECTION_HEADER_WHEN_STICKY}
			>
				{paragraphsHeader}
			</StickyWithCorrectType>
		);
	};

	const renderPdfOrDefaultSectionHeader = (): JSX.Element => {
		const sectionHeader: JSX.Element = renderBaseParagraphSectionHeader();
		/**
		 * We show the base paragraphs header when we are loading. Otherwise, it
		 * will show on top of the load wrapper, which looks weird.
		 */
		return isLoading || viewPdf
			? sectionHeader
			: renderDefaultParagraphSectionHeader(sectionHeader);
	};

	const getAmountToScrollBackBy = (): number => {
		if (viewPdf) return HEIGHT_OF_LIST_HEADER;
		return HEIGHT_OF_LIST_HEADER + HEIGHT_OF_PARAGRAPHS_SECTION_HEADER;
	};

	const isReqIfz =
		regulatoryDocumentDetails.attachments?.some(a =>
			a.file.originalFileName?.toLocaleLowerCase().endsWith('.reqifz'),
		) ?? false;

	const [paragraphsListKey, setParagraphsKeyList] = useState(0);

	const renderSectionHeaderAndParagraphs = (): JSX.Element => {
		return (
			<>
				{renderPdfOrDefaultSectionHeader()}
				<RegDocParagraphTooltipTranslationProvider>
					<EntityListContextProvider pageDetails={regulatoryDocumentDetails}>
						{viewGrid && (
							<ParagraphGridView
								regulatoryDocumentId={docId}
								paragraphsAll={
									regulatoryDocumentDetails.paragraphs as RegulatoryDocumentParagraph[]
								}
								paragraphsFiltered={paragraphListItems}
								setViewGrid={setViewGrid}
							/>
						)}
						{!viewGrid && (
							<RegDocDetailsPageParagraphsList
								key={paragraphsListKey}
								tableId='regDocDetailsParagraphsList'
								paragraphs={paragraphListItems}
								regulationId={regulatoryDocumentDetails?.regulation?.id ?? ''}
								regulatoryDocumentId={docId}
								parentId={docId}
								questionItem={regulatoryDocumentDetails}
								clearingAudits={regulatoryDocumentDetails.clearingAuditLogs}
								workflowStatus={regulatoryDocumentDetails.workflow?.status}
								onShouldVirtualize={() => !viewPdf}
								amountToScrollBackByAfterScrollingToParagraph={getAmountToScrollBackBy()}
								isReqIfz={isReqIfz}
							/>
						)}
					</EntityListContextProvider>
				</RegDocParagraphTooltipTranslationProvider>
			</>
		);
	};

	const renderParagraphsPane = (): JSX.Element => {
		return (
			<SidePaneForParagraphs extraStyle={pdfParagraphsListWrapper}>
				{renderSectionHeaderAndParagraphs()}
			</SidePaneForParagraphs>
		);
	};

	const renderMainParagraphs = (): JSX.Element => {
		return (
			<div
				className={mergeStyles(mainParagraphsWrapper)}
				ref={refToContainerOfMainParagraphs}
			>
				{renderSectionHeaderAndParagraphs()}
			</div>
		);
	};

	if (!isRegDocLoading && !data?.regulatoryDocument) {
		return (
			<h4 style={{marginTop: '50px', marginLeft: '20px'}}>
				{t('DocumentNotFound')}
			</h4>
		);
	}

	const onMetadataTransferred = (
		regDocId: string,
		items: IComparedItem[],
		selectedFields: ComparisonUpdateFields,
	) => {
		if (
			regulatoryDocumentDetails &&
			regDocId === regulatoryDocumentDetails.id
		) {
			items.forEach((item, index) => {
				regulatoryDocumentDetails.paragraphs[index] = updateParagraphMetadata(
					regulatoryDocumentDetails.paragraphs[
						index
					] as RegulatoryDocumentParagraph,
					(item as any).newParagraph as RegulatoryDocumentParagraph,
					selectedFields,
				);
			});
			setParagraphsKeyList(paragraphsListKey + 1);
		}
	};

	const StickyWithCorrectType = Sticky as unknown as CorrectStickyType;
	return (
		<RegDocDetailsPageInfoProvider
			isFormRefetchingRegDoc={isFormRefetchingRegDoc}
			isRegDocOrVexItemsLoading={isLoading}
			regDocNetworkStatus={regDocNetworkStatus}
			vexItemsNetworkStatus={vexItemsNetworkStatus}
			areVexItemsLoading={vexItemsLoading}
			regDoc={data}
			vexItems={vexItems}
		>
			<RegDocDetailsPageLoadWrapper>
				<RegulatoryDocumentsContextProvider
					regulatoryDocument={regulatoryDocumentDetails as any}
					sMode={SelectionMode.none}
				>
					<RegDocSidebarInfoProvider>
						{!sideBySide && !viewPdf && !viewGrid && (
							<>
								<StickyWithCorrectType
									stickyPosition={StickyPositionType.Header}
									stickyClassName={
										CLASS_TO_ADD_TO_PARAGRAPHS_SECTION_HEADER_WHEN_STICKY
									}
								>
									<Breadcrumb
										items={
											regulatoryDocumentDetails.regulation
												? allBreadCrumbItems
												: headlessBreadCrumbItems
										}
									/>
								</StickyWithCorrectType>
								<AuditLogsLoadingStatusProvider
									areAuditLogsLoading={areAuditLogsLoading}
								>
									<DetailsSection
										title={regulatoryDocumentDetails?.name}
										titleWithAudit={showAudit}
										initialFieldRows={regDocFieldRows}
										generalFieldLabels={generalTabFieldLabels}
										dateFieldLabels={dateFieldsLabels}
										referenceFieldLabels={referenceFieldLabels}
										useTabsForInitialFields={true}
										auditLogData={auditLogs}
									/>
								</AuditLogsLoadingStatusProvider>
							</>
						)}
						{!isPogisDocument && sideBySide ? (
							<Stack horizontal>
								<RegDocParagraphTooltipTranslationProvider>
									<ParagraphsComparisonPanel
										listId='firstList'
										paragraphs={
											showWorkbenchItems
												? vexWorkbenchItems
												: regulatoryDocumentDetails.paragraphs
										}
										tableId='regDocDetailsParagraphsList'
									/>
									<hr style={{margin: 10}} />
									<ParagraphsComparisonPanel
										listId='secondList'
										paragraphs={sideBySide.paragraphs}
										isSecondaryList={true}
										selectionMode={SelectionMode.single}
									/>
								</RegDocParagraphTooltipTranslationProvider>
							</Stack>
						) : (
							<Stack horizontal styles={viewPdf ? stackStyles : stackshit}>
								{viewPdf ? renderParagraphsPane() : renderMainParagraphs()}
								{viewPdf && regulatoryDocumentDetails.document?.uri && (
									<div style={{width: '50%'}}>
										<PdfViewer url={regulatoryDocumentDetails.document?.uri} />
									</div>
								)}
							</Stack>
						)}
						{!isPogisDocument && !viewGrid && (
							<>
								<WorkflowDialog
									regulatoryDocument={regulatoryDocumentDetails as any}
									refetchAuditLogs={refetchAuditLogsAndUpdateIsLoading}
								/>
								{regulatoryDocumentDetails?.workflow?.status !==
									WorkflowStatus.New && (
									<>
										<ViewParagraphsForm
											regulatoryDocument={regulatoryDocumentDetails}
										/>
										<EditParagraphsForm
											refetchAuditLogsAndUpdateIsLoading={
												refetchAuditLogsAndUpdateIsLoading
											}
											setAreAuditLogsLoading={setAreAuditLogsLoading}
											vexItems={vexItems}
											setIsFormRefetchingRegDoc={setIsFormRefetchingRegDoc}
											regDoc={regulatoryDocumentDetails}
											setIndexToScrollTo={setIndexToScrollTo}
										/>
										<AssignRequirementsForm />
										<RemoveRequirementsForm />
									</>
								)}
								<CommentForm
									comments={comments ?? []}
									level={CommentLevel.RegulatoryDocument}
								/>
								<FAQForm
									faqs={faqs ?? []}
									level={FAQLevel.RegulatoryDocument}
								/>
								<KeywordAssignmentForm />
								<CompareversionForm onValueChange={handleSideBySideChange} />
								<CompareVersionPanel
									regulationId={regulatoryDocumentDetails?.regulation?.id ?? ''}
									onTransferred={onMetadataTransferred}
								/>
								<RegulatoryDocumentsForm
									{...formInfo}
									regulatoryDocument={regulatoryDocumentDetails}
									regulationId={regulation?.id ?? null}
									regulatoryDocuments={sortedRegulatoryDocs}
									markets={
										(regulatoryDocumentDetails?.regulation?.markets ??
											[]) as Market[]
									}
									regulationNumber={regulation?.regulationNumber ?? ''}
									queriesToRefetchAfterSave={[
										GetRegulatoryDocumentDetailsDocument,
									]}
									fetchAndSetAuditLogs={refetchAuditLogsAndUpdateIsLoading}
								/>
							</>
						)}
						{!isPogisDocument && (
							<ParagraphTableOfContents
								isOpen={tocIsOpen}
								onDismiss={hideToc}
								visibleParagraphs={paragraphs || []}
								allParagraphs={regulatoryDocumentDetails.paragraphs}
							/>
						)}
					</RegDocSidebarInfoProvider>
				</RegulatoryDocumentsContextProvider>
			</RegDocDetailsPageLoadWrapper>
		</RegDocDetailsPageInfoProvider>
	);
};

interface HeaderProps {
	toggleToc: () => void;
	t: TFunction;
	showWorkbenchItems: boolean;
	textStyles: ITextStyles;
	filteredParagraphs: RegulatoryDocumentParagraph[];
}

const ParagraphsSectionHeader: React.FC<HeaderProps> = ({
	filteredParagraphs,
	showWorkbenchItems,
	t,
	textStyles,
	toggleToc,
}) => {
	return (
		<Stack horizontal styles={{root: {marginBottom: -5}}}>
			<IconButton
				iconProps={{iconName: 'BulletedTreeList'}}
				onClick={toggleToc}
				title={t('TableOfContents')}
			/>
			<Text styles={textStyles} variant={'xLarge'}>
				{showWorkbenchItems ? t('MyParagraphsHeader') : t('ParagraphsHeader')}
			</Text>
			{filteredParagraphs.length > 0 && (
				<Icon
					iconName='Filter'
					title={t('ParagraphsFiltered')}
					styles={{root: {paddingTop: 8, paddingLeft: 8}}}
				/>
			)}
		</Stack>
	);
};

const getStyles = (fullHeight: boolean) => {
	const stackHeight: IRawStyle['height'] = getStackHeight(fullHeight);

	return {
		pdfParagraphsListWrapper: {width: '50%'},
		mainParagraphsWrapper: {},
		textStyles: {
			root: {
				paddingLeft: 15,
			},
		},
		stackStyles: {root: {height: stackHeight}},
		stackshit: {
			root: {display: 'block'},
		},
	};
};

export default RegulatoryDocumentDetailsPage;
